import { Link } from "react-router-dom";
import type { FormEvent, ReactNode } from "react";
import { BrandLogo } from "./BrandLogo.js";

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="auth-bg flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/login" className="inline-block no-underline">
            <BrandLogo className="h-12 w-auto" />
          </Link>
          <h1 className="mt-4 text-xl font-semibold text-ink">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
        </div>
        <div className="rounded-2xl border border-line bg-surface/90 p-6 shadow-sm backdrop-blur sm:p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

export const fieldCls =
  "mt-1 w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm text-ink outline-none " +
  "placeholder:text-slate-400 focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20";

// Use this when a field shares a row with buttons. `fieldCls` includes the
// label-to-field spacing; retaining that margin in an inline row offsets the
// field from its neighbouring controls.
export const inlineFieldCls =
  "h-9 w-full rounded-lg border border-line bg-white px-3 py-0 text-sm text-ink outline-none " +
  "placeholder:text-slate-400 focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20";

export const labelCls = "block text-sm font-medium text-ink";

export const linkCls = "font-medium text-brand-700 underline-offset-2 hover:underline";
export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger"
    >
      {message}
    </div>
  );
}

export function SuccessBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="status"
      className="rounded-lg border border-success/20 bg-success/5 px-3 py-2 text-sm text-success"
    >
      {message}
    </div>
  );
}

export function AuthForm({
  onSubmit,
  children,
}: {
  onSubmit: (e: FormEvent) => void;
  children: ReactNode;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {children}
    </form>
  );
}
